// src/pages/subcontractors/SubcontractorWork.jsx
//
// Project Manager's page for tracking sub-contractor cost. This is the
// PRIMARY labour-cost tracking mechanism for this business (see
// subcontractorService.js header comment for full context) — most on-site
// work is done by sub-contractors, not company staff, so this page (not a
// company-staff attendance/DPR system) is where that cost gets captured.
//
// BILLING MODEL (matches real market practice — confirmed with the user):
// Subcontractors typically charge per labour HEADCOUNT, split across
// Skilled / Unskilled categories (rates differ per trade and per
// contractor), not a single flat daily rate. So:
//   - Each Assignment (subcontractor ↔ project) carries a RATE CARD —
//     one row per labour category, each with its own ₹/day rate. Not
//     hardcoded to exactly "Skilled"/"Unskilled" — the PM can add/remove
//     category rows freely (seeded with those two as sensible defaults).
//   - Each Work Log just needs a headcount per category + days — amount
//     is always derived (Σ category.count × category.rate) × days,
//     never manually guessed.
//   - A "Lump Sum" escape hatch exists for the minority of jobs genuinely
//     quoted as a flat price — headcount tracking doesn't apply there.
//
// FLOW: Select Project → Select (or quick-create) Subcontractor →
// Select existing Assignment for that pair, or create one (rate card set
// once, reused for every future log against this pairing) → Log a day's
// work (just headcounts) → financial_transactions entry created
// automatically (accrual, no separate approval gate — same trust level
// as Transport module).

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  fetchSubcontractors, createSubcontractor,
  fetchAssignmentsForProject, createAssignment,
  logSubcontractorWork, fetchAllWorkLogs, markAssignmentCompleted,
  fetchPendingConfirmationLogs, confirmSubcontractorWork, rejectSubcontractorWork,
} from "../../services/subcontractorService";

const STATUS_UI = {
  pending_manager_confirmation: { label: "Awaiting Confirmation", bg: "#e0e7ff", color: "#3730a3" },
  bill_confirmed_unpaid:        { label: "Bill Confirmed — Unpaid", bg: "#fef3c7", color: "#92400e" },
  rejected:                     { label: "Rejected",               bg: "#fee2e2", color: "#991b1b" },
  paid:                         { label: "Paid",                   bg: "#d1fae5", color: "#065f46" },
};

const DEFAULT_LABOUR_RATES = [
  { category: "Skilled",   rate_per_day: "" },
  { category: "Unskilled", rate_per_day: "" },
];

// Trade-wise measurement rate card default — one blank row to start.
// Unlike labour (Skilled/Unskilled are near-universal), trades vary too
// much across contractors (Piping/Ducting/Electrical/Painting/etc.) to
// seed with sensible defaults, so this just gives the PM an empty row
// to fill in via "+ Add Trade".
const DEFAULT_WORK_ITEMS = [
  { trade: "", unit: "Rmt", rate_per_unit: "" },
];

// Units reused from Item Master's existing unit list (Sqm/Rmt/Rft/Job/
// Lot, D-184) — no new enum introduced.
const MEASUREMENT_UNITS = ["Rmt", "Sqft", "Sqm", "Nos", "Job", "Lot"];

// Defaults Log Date to today — PM logs work daily, so pre-filling today's
// date removes a small but repeated bit of friction from the roz-ka-kaam
// flow (still fully editable if logging a past/different day).
const todayStr = () => new Date().toISOString().split("T")[0];

export default function SubcontractorWork() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const userId    = user.uid || user.id || "";
  const userName  = user.name || "";
  const role      = user.role || "";
  const isSiteEngineer = role === "site_engineer";
  const isManagerRole  = ["sm", "owner", "manager"].includes(role);

  const [tab, setTab] = useState("list");

  // ── LIST STATE ───────────────────────────────────────────────────────
  const [logs,        setLogs]        = useState([]);
  const [listLoading, setListLoading] = useState(true);

  // ── PENDING CONFIRMATION STATE (Manager-tier only) ──────────────────
  const [pendingLogs,        setPendingLogs]        = useState([]);
  const [pendingLoading,     setPendingLoading]     = useState(true);
  const [confirmingId,       setConfirmingId]       = useState(null); // log id currently being approved (disables its button)
  const [rejectingLog,       setRejectingLog]       = useState(null); // the log object being rejected, or null
  const [rejectReason,       setRejectReason]       = useState("");
  const [rejectSaving,       setRejectSaving]       = useState(false);

  // ── MASTER DATA ──────────────────────────────────────────────────────
  const [projects,       setProjects]       = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [assignments,    setAssignments]    = useState([]); // for selected project
  // Every VALID (non-legacy) assignment this subcontractor has for the
  // selected project — usually just one, but a subcontractor can genuinely
  // have more than one live commercial arrangement on the same project
  // (e.g. an old labour-rate assignment plus a newer lump-sum one for a
  // different scope, or a rate revision) — see "+ Use a different rate"
  // below. Sorted newest-first so the default pick is the latest one.
  const [matchingAssignments, setMatchingAssignments] = useState([]);

  // ── FORM STATE ───────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  // UI-only toggle — Amount shows as a read-only computed total by
  // default (labour mode), with a small "Edit" link to reveal the raw
  // input, so the field reads as "here's the answer" rather than "please
  // type something" when it's already been auto-calculated.
  const [showAmountInput, setShowAmountInput] = useState(false);

  const emptyForm = {
    project_id: "", project_name: "", site_id: "", site_name: "",
    subcontractor_id: "", subcontractor_name: "",
    is_new_subcontractor: false, new_sub_trade: "", new_sub_phone: "",
    assignment_id: "", is_new_assignment: false,
    scope_of_work: "",
    billing_mode: "labour",              // "labour" | "lump_sum" | "measurement"
    labour_rates: DEFAULT_LABOUR_RATES,  // rate card, only used when creating a new assignment
    lump_sum_rate: "",
    work_items: DEFAULT_WORK_ITEMS,      // trade-wise rate card, only used when creating a new assignment
    log_date: todayStr(), work_description: "", days: "1",
    labour_counts: {},                   // { [category]: countString } for the work-log form
    measurement_qtys: {},                // { [trade]: qtyString } for the work-log form
    amount: "",
    legacy_assignment_found: false,
    replacing_assignment_id: "", // set when overriding an existing valid assignment
    is_rate_revision: true,      // default assumption when replacing: same scope, rate just changed
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadProjects();
    loadSubcontractors();
    loadAllLogs();
    if (isManagerRole) loadPendingConfirmations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPendingConfirmations = async () => {
    setPendingLoading(true);
    try {
      const list = await fetchPendingConfirmationLogs(companyId);
      setPendingLogs(list);
    } catch (err) {
      console.error("fetchPendingConfirmationLogs error:", err);
    } finally {
      setPendingLoading(false);
    }
  };

  // ── APPROVE (Stage 2a) ──────────────────────────────────────────────
  const handleApprove = async (log) => {
    if (!window.confirm(`Confirm ${log.subcontractor_name}'s bill of ₹${Number(log.amount || 0).toLocaleString()}? This records it as owed (unpaid) — Accounts will mark it paid separately.`)) return;
    try {
      setConfirmingId(log.id);
      await confirmSubcontractorWork({
        log_id: log.id, company_id: companyId,
        confirmed_by: userId, confirmed_by_name: userName,
      });
      await loadPendingConfirmations();
      await loadAllLogs();
    } catch (err) {
      alert(err.message || "Failed to confirm");
    } finally {
      setConfirmingId(null);
    }
  };

  // ── REJECT (Stage 2b) ────────────────────────────────────────────────
  const openRejectModal  = (log) => { setRejectingLog(log); setRejectReason(""); };
  const closeRejectModal = () => { setRejectingLog(null); setRejectReason(""); };

  const handleReject = async () => {
    if (!rejectReason.trim()) { alert("Reason is mandatory."); return; }
    try {
      setRejectSaving(true);
      await rejectSubcontractorWork({
        log_id: rejectingLog.id, company_id: companyId,
        rejected_by: userId, rejected_by_name: userName,
        rejection_reason: rejectReason,
      });
      closeRejectModal();
      await loadPendingConfirmations();
      await loadAllLogs();
    } catch (err) {
      alert(err.message || "Failed to reject");
    } finally {
      setRejectSaving(false);
    }
  };

  const loadAllLogs = async () => {
    setListLoading(true);
    try {
      const list = await fetchAllWorkLogs(companyId);
      setLogs(list);
    } catch (err) {
      console.error("fetchAllWorkLogs error:", err);
    } finally {
      setListLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      // Site Engineers are scoped to only the projects the Project Manager
      // has assigned them to (projects.site_engineer_ids array-contains
      // their uid). Technicians are scoped differently — via their
      // assigned service_tasks (they aren't listed on the project doc
      // directly, same pattern used for their other project-facing pages
      // elsewhere in this app) — so for a technician here we first find
      // the distinct project_ids from their own service_tasks, then
      // filter the projects list to just those. sm/owner/manager continue
      // to see every project.
      if (isSiteEngineer) {
        const snap = await getDocs(query(
          collection(db, "projects"),
          where("company_id", "==", companyId),
          where("site_engineer_ids", "array-contains", userId)
        ));
        setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        return;
      }
      if (role === "technician") {
        // assigned_techs is an array of OBJECTS ({id, accepted, ...}), not
        // an array of uid strings — Firestore's array-contains can only
        // match an exact primitive, never a field inside an array's
        // objects. So this can't be a server-side query; fetch this
        // company's service_tasks (already company_id-scoped) and filter
        // client-side — same pattern MainLayout.js's OEM-pending-count
        // fetch already uses for this exact same field shape.
        const taskSnap = await getDocs(query(
          collection(db, "service_tasks"),
          where("company_id", "==", companyId)
        ));
        const projectIds = [...new Set(
          taskSnap.docs
            .filter(d => (d.data().assigned_techs || []).some(t => t.id === userId))
            .map(d => d.data().project_id)
            .filter(Boolean)
        )];
        if (projectIds.length === 0) { setProjects([]); return; }
        // Firestore "in" caps at 30 values per query — fine for a single
        // technician's realistic active-project count; batching isn't
        // needed here the way it would be for a company-wide query.
        const projSnap = await getDocs(query(
          collection(db, "projects"),
          where("company_id", "==", companyId),
          where("__name__", "in", projectIds.slice(0, 30))
        ));
        setProjects(projSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        return;
      }
      const snap = await getDocs(query(
        collection(db, "projects"),
        where("company_id", "==", companyId)
      ));
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  const loadSubcontractors = async () => {
    try {
      const list = await fetchSubcontractors(companyId);
      setSubcontractors(list);
    } catch (err) { console.error(err); }
  };

  const handleProjectChange = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    setMatchingAssignments([]);
    setForm(f => ({
      ...f,
      project_id: projectId,
      project_name: project?.project_name || "",
      site_id: project?.site_id || "",
      site_name: project?.site_name || "",
      subcontractor_id: "", subcontractor_name: "",
      assignment_id: "", is_new_assignment: false,
    }));
    if (!projectId) { setAssignments([]); return; }
    try {
      const list = await fetchAssignmentsForProject(companyId, projectId);
      setAssignments(list);
    } catch (err) {
      console.error("fetchAssignmentsForProject error:", err);
      setAssignments([]);
    }
  };

  const hasValidRateCard = (a) => a && (
    a.billing_mode === "lump_sum"
      ? Number(a.lump_sum_rate) > 0
      : a.billing_mode === "measurement"
        ? Array.isArray(a.work_items) && a.work_items.some(w => Number(w.rate_per_unit) > 0)
        : Array.isArray(a.labour_rates) && a.labour_rates.some(r => Number(r.rate_per_day) > 0)
  );

  const handleSubcontractorChange = (subId) => {
    setShowAmountInput(false);
    if (subId === "__new__") {
      setMatchingAssignments([]);
      setForm(f => ({
        ...f,
        subcontractor_id: "", subcontractor_name: "",
        is_new_subcontractor: true,
        assignment_id: "", is_new_assignment: false,
        billing_mode: "labour",
        labour_rates: DEFAULT_LABOUR_RATES,
        lump_sum_rate: "",
        work_items: DEFAULT_WORK_ITEMS,
        labour_counts: {},
        measurement_qtys: {},
        amount: "",
        legacy_assignment_found: false,
        replacing_assignment_id: "",
      }));
      return;
    }
    const sub = subcontractors.find(s => s.id === subId);
    const allMatches = assignments.filter(a => a.subcontractor_id === subId);

    // Assignments created before this session's rate-card rewrite carry
    // the old rate_type/rate_amount schema (no labour_rates/billing_mode
    // at all). Treat those the same as "no assignment" — auto-fall-back
    // to creating a fresh one, rather than silently working with a ₹0
    // rate card and producing a confusing zero-amount log. The old doc
    // is left as-is (dead-but-harmless legacy data), matching how this
    // app already treats every other legacy-schema field elsewhere.
    const validMatches = allMatches
      .filter(hasValidRateCard)
      .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
    const isLegacyOnly = allMatches.length > 0 && validMatches.length === 0;

    setMatchingAssignments(validMatches);

    const defaultAssignment = validMatches[0]; // most recent valid one

    setForm(f => ({
      ...f,
      subcontractor_id: subId,
      subcontractor_name: sub?.name || "",
      is_new_subcontractor: false,
      assignment_id: defaultAssignment ? defaultAssignment.id : "",
      is_new_assignment: !defaultAssignment,
      billing_mode: defaultAssignment ? defaultAssignment.billing_mode : "labour",
      labour_rates: defaultAssignment?.labour_rates?.length
        ? defaultAssignment.labour_rates
        : DEFAULT_LABOUR_RATES,
      lump_sum_rate: defaultAssignment ? (defaultAssignment.lump_sum_rate || "") : "",
      work_items: defaultAssignment?.work_items?.length
        ? defaultAssignment.work_items
        : DEFAULT_WORK_ITEMS,
      labour_counts: {},
      measurement_qtys: {},
      amount: "",
      legacy_assignment_found: isLegacyOnly,
      replacing_assignment_id: "",
    }));
  };
  // PM picks a different one of this subcontractor's multiple valid
  // assignments (e.g. an older labour-rate one vs a newer lump-sum one).
  const selectAssignment = (assignmentId) => {
    const a = matchingAssignments.find(x => x.id === assignmentId);
    if (!a) return;
    setShowAmountInput(false);
    setForm(f => ({
      ...f,
      assignment_id: a.id,
      is_new_assignment: false,
      billing_mode: a.billing_mode,
      labour_rates: a.labour_rates?.length ? a.labour_rates : DEFAULT_LABOUR_RATES,
      lump_sum_rate: a.lump_sum_rate || "",
      work_items: a.work_items?.length ? a.work_items : DEFAULT_WORK_ITEMS,
      labour_counts: {},
      measurement_qtys: {},
      amount: "",
    }));
  };

  // "Use a different rate" override — always available even when a valid
  // assignment was found. Covers both real-world cases: the negotiated
  // rate changed (start a fresh rate card), or the PM wants to bill this
  // same subcontractor's next chunk of work as a lump sum instead of
  // headcount-based. Creates a brand-new assignment doc on submit — the
  // old one (and every log already made against it) is untouched.
  const startNewRateForSameSubcontractor = () => {
    setShowAmountInput(false);
    setForm(f => ({
      ...f,
      replacing_assignment_id: f.assignment_id, // remember what's being replaced
      is_rate_revision: true,                   // default guess: same scope, rate just changed
      assignment_id: "",
      is_new_assignment: true,
      billing_mode: "labour",
      labour_rates: DEFAULT_LABOUR_RATES,
      lump_sum_rate: "",
      work_items: DEFAULT_WORK_ITEMS,
      labour_counts: {},
      measurement_qtys: {},
      amount: "",
    }));
  };

  // Amount = Σ(category.count × category.rate_per_day) × days — for
  // labour billing mode. Recomputed on every headcount/day change so the
  // PM always sees the live total, never has to do the maths themselves.
  const recalcLabourAmount = (labourRates, labourCounts, days) => {
    const total = (labourRates || []).reduce((sum, r) => {
      const count = Number(labourCounts?.[r.category] || 0);
      const rate  = Number(r.rate_per_day || 0);
      return sum + count * rate;
    }, 0);
    return String(total * Number(days || 0));
  };

  // Amount = Σ(trade.qty × trade.rate_per_unit) — for measurement billing
  // mode. No "days" multiplier here (unlike labour) since qty already IS
  // the confirmed measured work for this log (e.g. 40 Rmt of piping done
  // this visit), not a daily rate to be multiplied further.
  const recalcMeasurementAmount = (workItems, measurementQtys) => {
    const total = (workItems || []).reduce((sum, w) => {
      const qty  = Number(measurementQtys?.[w.trade] || 0);
      const rate = Number(w.rate_per_unit || 0);
      return sum + qty * rate;
    }, 0);
    return String(total);
  };

  // ── New-assignment rate card editing (add/remove/edit category rows) ──
  const updateNewAssignmentRateRow = (index, field, value) => {
    setForm(f => {
      const updated = [...f.labour_rates];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, labour_rates: updated };
    });
  };
  const addRateRow = () => {
    setForm(f => ({ ...f, labour_rates: [...f.labour_rates, { category: "", rate_per_day: "" }] }));
  };
  const removeRateRow = (index) => {
    setForm(f => ({ ...f, labour_rates: f.labour_rates.filter((_, i) => i !== index) }));
  };

  // ── New-assignment measurement rate card editing (add/remove/edit trade rows) ──
  const updateNewAssignmentWorkItemRow = (index, field, value) => {
    setForm(f => {
      const updated = [...f.work_items];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, work_items: updated };
    });
  };
  const addWorkItemRow = () => {
    setForm(f => ({ ...f, work_items: [...f.work_items, { trade: "", unit: "Rmt", rate_per_unit: "" }] }));
  };
  const removeWorkItemRow = (index) => {
    setForm(f => ({ ...f, work_items: f.work_items.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async () => {
    if (!form.project_id)          { alert("Select a project"); return; }
    if (!form.is_new_subcontractor && !form.subcontractor_id) { alert("Select or add a subcontractor"); return; }
    if (form.is_new_subcontractor && !form.subcontractor_name.trim()) { alert("Enter subcontractor name"); return; }
    if (!form.log_date)            { alert("Log date required"); return; }
    if (!form.work_description.trim()) { alert("Work description required"); return; }

    if (form.is_new_assignment) {
      if (form.billing_mode === "labour") {
        const validRows = form.labour_rates.filter(r => r.category.trim() && Number(r.rate_per_day) > 0);
        if (validRows.length === 0) { alert("Add at least one labour category with a rate"); return; }
      } else if (form.billing_mode === "measurement") {
        const validRows = form.work_items.filter(w => w.trade.trim() && Number(w.rate_per_unit) > 0);
        if (validRows.length === 0) { alert("Add at least one trade with a rate"); return; }
      } else if (!form.lump_sum_rate || Number(form.lump_sum_rate) <= 0) {
        alert("Set the lump sum rate"); return;
      }
    }

    if (!form.amount || Number(form.amount) <= 0) { alert("Valid amount required"); return; }

    setSaving(true);
    try {
      let subcontractorId   = form.subcontractor_id;
      let subcontractorName = form.subcontractor_name;

      if (form.is_new_subcontractor) {
        subcontractorId = await createSubcontractor({
          company_id: companyId,
          name: form.subcontractor_name,
          trade: form.new_sub_trade,
          phone: form.new_sub_phone,
          created_by: userId,
          created_by_name: userName,
        });
        subcontractorName = form.subcontractor_name.trim();
      }

      let assignmentId = form.assignment_id;
      let effectiveLabourRates = form.labour_rates;
      let effectiveWorkItems = form.work_items;

      if (form.is_new_assignment || !assignmentId) {
        assignmentId = await createAssignment({
          company_id: companyId,
          subcontractor_id: subcontractorId,
          subcontractor_name: subcontractorName,
          project_id: form.project_id,
          project_name: form.project_name,
          site_id: form.site_id,
          site_name: form.site_name,
          scope_of_work: form.scope_of_work,
          billing_mode: form.billing_mode,
          labour_rates: form.labour_rates,
          lump_sum_rate: form.lump_sum_rate,
          work_items: form.work_items,
          created_by: userId,
          created_by_name: userName,
        });

        // Same-scope rate revision — retire the old assignment so it
        // stops appearing in the picker going forward. Its own logs and
        // financial_transactions entries are completely untouched; this
        // ONLY changes whether it's offered as an option for NEW logs.
        // If the PM instead said this is a genuinely different scope of
        // work, both assignments stay active side by side on purpose.
        if (form.replacing_assignment_id && form.is_rate_revision) {
          try {
            await markAssignmentCompleted(form.replacing_assignment_id, companyId);
          } catch (err) {
            console.warn("Could not mark old assignment completed (non-blocking):", err.message);
          }
        }
      } else {
        // Existing assignment — use its already-saved rate card so the
        // work log records the rate that was actually agreed, not
        // whatever happens to be in the (unused) new-assignment form state.
        const existing = assignments.find(a => a.id === assignmentId);
        effectiveLabourRates = existing?.labour_rates || form.labour_rates;
        effectiveWorkItems = existing?.work_items || form.work_items;
      }

      const labourBreakdown = form.billing_mode === "labour"
        ? effectiveLabourRates
            .filter(r => r.category.trim())
            .map(r => ({
              category: r.category,
              rate_per_day: Number(r.rate_per_day || 0),
              count: Number(form.labour_counts[r.category] || 0),
            }))
        : [];

      const measurementBreakdown = form.billing_mode === "measurement"
        ? effectiveWorkItems
            .filter(w => w.trade.trim())
            .map(w => ({
              trade: w.trade,
              unit: w.unit,
              rate_per_unit: Number(w.rate_per_unit || 0),
              qty: Number(form.measurement_qtys[w.trade] || 0),
            }))
        : [];

      await logSubcontractorWork({
        company_id: companyId,
        assignment_id: assignmentId,
        subcontractor_id: subcontractorId,
        subcontractor_name: subcontractorName,
        project_id: form.project_id,
        project_name: form.project_name,
        site_id: form.site_id,
        site_name: form.site_name,
        log_date: form.log_date,
        work_description: form.work_description,
        billing_mode: form.billing_mode,
        days: form.days,
        labour_breakdown: labourBreakdown,
        measurement_breakdown: measurementBreakdown,
        amount: form.amount,
        logged_by: userId,
        logged_by_name: userName,
      });

      setForm(emptyForm);
      setShowAmountInput(false);
      setTab("list");
      loadAllLogs();
      loadSubcontractors();
    } catch (err) {
      alert(err.message || "Failed to save work log");
    } finally {
      setSaving(false);
    }
  };

  const activeRateCard = form.labour_rates.filter(r => r.category.trim());
  const activeWorkItems = form.work_items.filter(w => w.trade.trim());

  return (
    <div className="p-4 pb-24 bg-gray-50 min-h-screen max-w-2xl mx-auto">

      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <h1 className="text-xl font-bold text-slate-800">Subcontractor Work</h1>
        {tab === "list" ? (
          <div className="flex gap-2">
            {isManagerRole && (
              <button onClick={() => setTab("confirm")}
                className="relative bg-indigo-100 text-indigo-700 px-4 py-2 rounded-xl text-sm font-semibold">
                ⏳ Pending Confirmation
                {pendingLogs.length > 0 && (
                  <span className="ml-1.5 bg-indigo-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
                    {pendingLogs.length}
                  </span>
                )}
              </button>
            )}
            <button onClick={() => setTab("create")}
              className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold">
              + Log Work
            </button>
          </div>
        ) : (
          <button onClick={() => { setTab("list"); setForm(emptyForm); setShowAmountInput(false); }}
            className="text-sm text-slate-500 hover:text-slate-800">
            ← Back
          </button>
        )}
      </div>

      {/* ── LIST ── */}
      {tab === "list" && (
        listLoading ? (
          <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
        ) : logs.length === 0 ? (
          <div className="text-center mt-16">
            <div className="text-4xl mb-3">👷</div>
            <p className="text-slate-500 font-medium">No work logged yet</p>
            <p className="text-slate-400 text-sm mt-1">Tap + Log Work to add one</p>
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map(log => {
              const statusUI = STATUS_UI[log.status] || STATUS_UI.pending_manager_confirmation;
              const breakdown = (log.labour_breakdown || []).filter(b => b.count > 0);
              const measurementBreakdown = (log.measurement_breakdown || []).filter(m => m.qty > 0);
              return (
                <div key={log.id}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
                  style={{ borderLeft: `4px solid ${statusUI.color}` }}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-800">👷 {log.subcontractor_name}</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{ background: statusUI.bg, color: statusUI.color }}>
                          {statusUI.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {log.log_date} · 🏗 {log.project_name}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">{log.work_description}</p>
                      {breakdown.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {breakdown.map(b => `${b.count} ${b.category}`).join(", ")} × {log.days} day{Number(log.days) === 1 ? "" : "s"}
                        </p>
                      )}
                      {measurementBreakdown.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {measurementBreakdown.map(m => `${m.qty} ${m.unit} ${m.trade}`).join(", ")}
                        </p>
                      )}
                    </div>
                    <p className="text-lg font-black text-slate-800 ml-3 shrink-0">
                      ₹{Number(log.amount || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── PENDING CONFIRMATION (Manager-tier only, Stage 2) ── */}
      {tab === "confirm" && isManagerRole && (
        pendingLoading ? (
          <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
        ) : pendingLogs.length === 0 ? (
          <div className="text-center mt-16">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-slate-500 font-medium">Nothing awaiting confirmation</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingLogs.map(log => {
              const breakdown = (log.labour_breakdown || []).filter(b => b.count > 0);
              const measurementBreakdown = (log.measurement_breakdown || []).filter(m => m.qty > 0);
              return (
                <div key={log.id}
                  className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800">👷 {log.subcontractor_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {log.log_date} · 🏗 {log.project_name} · reported by {log.logged_by_name || "—"}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">{log.work_description}</p>
                      {breakdown.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {breakdown.map(b => `${b.count} ${b.category}`).join(", ")} × {log.days} day{Number(log.days) === 1 ? "" : "s"}
                        </p>
                      )}
                      {measurementBreakdown.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {measurementBreakdown.map(m => `${m.qty} ${m.unit} ${m.trade}`).join(", ")}
                        </p>
                      )}
                    </div>
                    <p className="text-lg font-black text-slate-800 ml-3 shrink-0">
                      ₹{Number(log.amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => handleApprove(log)}
                      disabled={confirmingId === log.id}
                      className="flex-1 py-2 rounded-xl bg-green-600 text-white text-xs font-semibold disabled:opacity-50">
                      {confirmingId === log.id ? "Confirming..." : "✓ Confirm Bill"}
                    </button>
                    <button onClick={() => openRejectModal(log)}
                      disabled={confirmingId === log.id}
                      className="flex-1 py-2 rounded-xl bg-red-100 text-red-700 text-xs font-semibold disabled:opacity-50">
                      ✕ Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── REJECT MODAL ── */}
      {rejectingLog && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5">
            <h3 className="font-bold text-slate-800 mb-1">✕ Reject Bill</h3>
            <p className="text-xs text-slate-500 mb-3">
              {rejectingLog.subcontractor_name} — ₹{Number(rejectingLog.amount || 0).toLocaleString()}.
              No bill will be recorded. Reason is mandatory.
            </p>
            <textarea rows={3} value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Why is this being rejected?"
              autoFocus
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none resize-none" />
            <div className="flex gap-3 mt-4">
              <button onClick={closeRejectModal}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-slate-600">
                Cancel
              </button>
              <button onClick={handleReject} disabled={rejectSaving || !rejectReason.trim()}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-50">
                {rejectSaving ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CREATE ── */}
      {tab === "create" && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Project *</label>
            <select value={form.project_id}
              onChange={e => handleProjectChange(e.target.value)}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-blue-300">
              <option value="">Select project</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.project_name} {p.site_name ? `— ${p.site_name}` : ""}
                </option>
              ))}
            </select>
          </div>

          {form.project_id && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Subcontractor *</label>
              <select value={form.is_new_subcontractor ? "__new__" : form.subcontractor_id}
                onChange={e => handleSubcontractorChange(e.target.value)}
                className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-blue-300">
                <option value="">Select subcontractor</option>
                {subcontractors.map(s => (
                  <option key={s.id} value={s.id}>{s.name} {s.trade ? `(${s.trade})` : ""}</option>
                ))}
                <option value="__new__">+ Add New Subcontractor</option>
              </select>

              {form.is_new_subcontractor && (
                <div className="mt-2 space-y-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <input placeholder="Subcontractor Name *"
                    value={form.subcontractor_name}
                    onChange={e => setForm(f => ({ ...f, subcontractor_name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg p-2 text-sm outline-none" />
                  <input placeholder="Trade (e.g. Electrical, Plumbing)"
                    value={form.new_sub_trade}
                    onChange={e => setForm(f => ({ ...f, new_sub_trade: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg p-2 text-sm outline-none" />
                  <input placeholder="Phone (optional)"
                    value={form.new_sub_phone}
                    onChange={e => setForm(f => ({ ...f, new_sub_phone: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg p-2 text-sm outline-none" />
                </div>
              )}
            </div>
          )}

          {/* Assignment / Rate Card */}
          {form.project_id && (form.subcontractor_id || form.is_new_subcontractor) && (
            <div>
              {form.legacy_assignment_found && (
                <p className="text-xs text-red-600 font-semibold px-3 py-2 bg-red-50 rounded-lg border border-red-200 mb-2">
                  ⚠️ Found a previous assignment for this subcontractor, but it has no valid rate set (old format). Creating a fresh assignment below — please set the rate card.
                </p>
              )}
              {!form.is_new_assignment ? (
                <div className="px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-600 font-semibold mb-1">✓ Existing assignment</p>

                  {/* This subcontractor has more than one valid live
                      arrangement on this project (e.g. an old rate plus a
                      rate revision, or a labour-based one alongside a
                      lump-sum one for different scope) — let the PM pick
                      which to log this entry against. */}
                  {matchingAssignments.length > 1 && (
                    <select value={form.assignment_id}
                      onChange={e => selectAssignment(e.target.value)}
                      className="w-full border border-emerald-200 rounded-lg p-2 text-xs outline-none bg-white mb-1">
                      {matchingAssignments.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.billing_mode === "lump_sum"
                            ? `Lump Sum @ ₹${a.lump_sum_rate}${a.scope_of_work ? " — " + a.scope_of_work : ""}`
                            : a.billing_mode === "measurement"
                              ? `${(a.work_items || []).map(w => `${w.trade}: ₹${w.rate_per_unit}/${w.unit}`).join(", ")}${a.scope_of_work ? " — " + a.scope_of_work : ""}`
                              : `${(a.labour_rates || []).map(r => `${r.category}: ₹${r.rate_per_day}/day`).join(", ")}${a.scope_of_work ? " — " + a.scope_of_work : ""}`}
                        </option>
                      ))}
                    </select>
                  )}

                  {matchingAssignments.length <= 1 && (
                    form.billing_mode === "labour" ? (
                      <p className="text-xs text-emerald-700">
                        {activeRateCard.map(r => `${r.category}: ₹${r.rate_per_day}/day`).join(" · ")}
                      </p>
                    ) : form.billing_mode === "measurement" ? (
                      <p className="text-xs text-emerald-700">
                        {activeWorkItems.map(w => `${w.trade}: ₹${w.rate_per_unit}/${w.unit}`).join(" · ")}
                      </p>
                    ) : (
                      <p className="text-xs text-emerald-700">Lump Sum @ ₹{form.lump_sum_rate}</p>
                    )
                  )}

                  {/* Always available — covers both real scenarios: the
                      negotiated rate changed, or the PM wants to bill this
                      subcontractor's next chunk of work differently (e.g.
                      switch to Lump Sum). Starts a brand-new assignment;
                      the one shown above (and every log against it) is
                      never touched. */}
                  <button type="button" onClick={startNewRateForSameSubcontractor}
                    className="text-[11px] text-emerald-700 underline font-semibold mt-1">
                    Rate changed, or need a different billing arrangement? Start a new rate →
                  </button>
                </div>
              ) : (
                <div className="space-y-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-xs font-semibold text-amber-700">New assignment — set the rate once (reused for future logs)</p>

                  {/* Only relevant when this is replacing a previous valid
                      assignment for this same subcontractor. Determines
                      whether the old one gets retired from the picker
                      (rate revision) or stays active alongside the new one
                      (genuinely different, concurrent scope of work) —
                      this is what keeps the assignment picker from growing
                      indefinitely over years of routine rate changes. */}
                  {form.replacing_assignment_id && (
                    <div className="flex gap-2">
                      {[
                        { val: true,  label: "Same scope — rate just changed" },
                        { val: false, label: "Different scope of work" },
                      ].map(opt => (
                        <button key={String(opt.val)} type="button"
                          onClick={() => setForm(f => ({ ...f, is_rate_revision: opt.val }))}
                          className={`flex-1 py-2 rounded-lg text-[11px] font-semibold border ${
                            form.is_rate_revision === opt.val
                              ? "border-amber-500 bg-amber-100 text-amber-800"
                              : "border-slate-200 bg-white text-slate-500"
                          }`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <input placeholder="Scope of work (e.g. Ducting & piping)"
                    value={form.scope_of_work}
                    onChange={e => setForm(f => ({ ...f, scope_of_work: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg p-2 text-sm outline-none" />

                   {/* Billing mode toggle */}
                  <div className="flex gap-2">
                    {[
                      { val: "labour",      label: "Labour-based (headcount)" },
                      { val: "measurement", label: "Trade-wise (RMT/Sqft)" },
                      { val: "lump_sum",    label: "Lump Sum / Per Job" },
                    ].map(opt => (
                      <button key={opt.val} type="button"
                        onClick={() => setForm(f => ({ ...f, billing_mode: opt.val }))}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold border ${
                          form.billing_mode === opt.val
                            ? "border-amber-500 bg-amber-100 text-amber-800"
                            : "border-slate-200 bg-white text-slate-500"
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {form.billing_mode === "labour" ? (
                    <div className="space-y-2">
                      <p className="text-[11px] text-amber-700 font-semibold uppercase">Rate Card</p>
                      {form.labour_rates.map((row, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input placeholder="Category (e.g. Skilled)"
                            value={row.category}
                            onChange={e => updateNewAssignmentRateRow(i, "category", e.target.value)}
                            className="flex-1 border border-slate-200 rounded-lg p-2 text-sm outline-none" />
                          <input type="number" placeholder="₹ / day"
                            value={row.rate_per_day}
                            onChange={e => updateNewAssignmentRateRow(i, "rate_per_day", e.target.value)}
                            className="w-28 border border-slate-200 rounded-lg p-2 text-sm outline-none" />
                          {form.labour_rates.length > 1 && (
                            <button type="button" onClick={() => removeRateRow(i)}
                              className="text-red-400 text-sm px-1">✕</button>
                          )}
                        </div>
                      ))}
                      <button type="button" onClick={addRateRow}
                        className="text-xs text-amber-700 font-semibold">+ Add Category</button>
                    </div>
                  ) : form.billing_mode === "measurement" ? (
                    <div className="space-y-2">
                      <p className="text-[11px] text-amber-700 font-semibold uppercase">Trade-wise Rate Card</p>
                      {form.work_items.map((row, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input placeholder="Trade (e.g. Piping)"
                            value={row.trade}
                            onChange={e => updateNewAssignmentWorkItemRow(i, "trade", e.target.value)}
                            className="flex-1 border border-slate-200 rounded-lg p-2 text-sm outline-none" />
                          <select value={row.unit}
                            onChange={e => updateNewAssignmentWorkItemRow(i, "unit", e.target.value)}
                            className="w-24 border border-slate-200 rounded-lg p-2 text-sm outline-none bg-white">
                            {MEASUREMENT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                          <input type="number" placeholder="₹ / unit"
                            value={row.rate_per_unit}
                            onChange={e => updateNewAssignmentWorkItemRow(i, "rate_per_unit", e.target.value)}
                            className="w-24 border border-slate-200 rounded-lg p-2 text-sm outline-none" />
                          {form.work_items.length > 1 && (
                            <button type="button" onClick={() => removeWorkItemRow(i)}
                              className="text-red-400 text-sm px-1">✕</button>
                          )}
                        </div>
                      ))}
                      <button type="button" onClick={addWorkItemRow}
                        className="text-xs text-amber-700 font-semibold">+ Add Trade</button>
                    </div>
                  ) : (
                    <input type="number" placeholder="Lump Sum Rate ₹"
                      value={form.lump_sum_rate}
                      onChange={e => setForm(f => ({ ...f, lump_sum_rate: e.target.value, amount: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg p-2 text-sm outline-none" />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Work log details */}
          {form.project_id && (form.subcontractor_id || form.is_new_subcontractor) && (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Log Date *</label>
                <input type="date" value={form.log_date}
                  onChange={e => setForm(f => ({ ...f, log_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Work Description *</label>
                {/* Quick-chips — recent unique descriptions logged for THIS
                    subcontractor (from data already loaded for the list
                    view, no extra query). Tap to reuse, still editable
                    after — saves retyping the same "Ducting work" /
                    "Piping" every day. */}
                {(() => {
                  const recentDescriptions = form.subcontractor_id
                    ? [...new Set(
                        logs
                          .filter(l => l.subcontractor_id === form.subcontractor_id && l.work_description)
                          .map(l => l.work_description)
                      )].slice(0, 5)
                    : [];
                  return recentDescriptions.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {recentDescriptions.map((desc, i) => (
                        <button key={i} type="button"
                          onClick={() => setForm(f => ({ ...f, work_description: desc }))}
                          className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition">
                          {desc.length > 28 ? desc.slice(0, 28) + "…" : desc}
                        </button>
                      ))}
                    </div>
                  ) : null;
                })()}
                <textarea rows={2} placeholder="What was done during this update/visit?"
                  value={form.work_description}
                  onChange={e => setForm(f => ({ ...f, work_description: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none resize-none focus:ring-2 focus:ring-blue-300" />
              </div>

              {form.billing_mode === "labour" ? (
                <>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Days</label>
                    <input type="number" min="0" value={form.days}
                      onChange={e => setForm(f => ({
                        ...f, days: e.target.value,
                        amount: recalcLabourAmount(activeRateCard, f.labour_counts, e.target.value),
                      }))}
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                  </div>

                  {/* One headcount input per rate-card category — this is
                      the ONLY thing the PM needs to enter day to day; the
                      rate itself was fixed once at assignment time. */}
                  <div className="grid grid-cols-2 gap-3">
                    {activeRateCard.map(row => (
                      <div key={row.category}>
                        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                          {row.category} Count
                        </label>
                        <input type="number" min="0"
                          value={form.labour_counts[row.category] || ""}
                          onChange={e => {
                            const updatedCounts = { ...form.labour_counts, [row.category]: e.target.value };
                            setForm(f => ({
                              ...f, labour_counts: updatedCounts,
                              amount: recalcLabourAmount(activeRateCard, updatedCounts, f.days),
                            }));
                          }}
                          className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                      </div>
                    ))}
                  </div>

                  {/* Live running-total — confirms the math right where the
                      PM is looking, without needing to scroll down to the
                      Amount field to see what got computed. */}
                  {activeRateCard.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <span className="text-sm font-bold text-blue-800">
                        = ₹{Number(form.amount || 0).toLocaleString()}
                      </span>
                      <span className="text-xs text-blue-500 ml-2">
                        ({activeRateCard.map(r => {
                          const count = Number(form.labour_counts[r.category] || 0);
                          const subtotal = count * Number(r.rate_per_day || 0) * Number(form.days || 0);
                          return `${r.category}: ₹${subtotal.toLocaleString()}`;
                        }).join(" + ")})
                      </span>
                    </div>
                  )}
                </>
              ) : form.billing_mode === "measurement" ? (
                <>
                  {/* One qty input per trade — no "days" field here, unlike
                      labour mode, since qty already IS the confirmed
                      measured work for this visit (e.g. 40 Rmt of piping),
                      not a daily rate needing further multiplication. */}
                  <div className="grid grid-cols-2 gap-3">
                    {activeWorkItems.map(row => (
                      <div key={row.trade}>
                        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                          {row.trade} ({row.unit})
                        </label>
                        <input type="number" min="0"
                          value={form.measurement_qtys[row.trade] || ""}
                          onChange={e => {
                            const updatedQtys = { ...form.measurement_qtys, [row.trade]: e.target.value };
                            setForm(f => ({
                              ...f, measurement_qtys: updatedQtys,
                              amount: recalcMeasurementAmount(activeWorkItems, updatedQtys),
                            }));
                          }}
                          className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                      </div>
                    ))}
                  </div>

                  {activeWorkItems.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <span className="text-sm font-bold text-blue-800">
                        = ₹{Number(form.amount || 0).toLocaleString()}
                      </span>
                      <span className="text-xs text-blue-500 ml-2">
                        ({activeWorkItems.map(w => {
                          const qty = Number(form.measurement_qtys[w.trade] || 0);
                          const subtotal = qty * Number(w.rate_per_unit || 0);
                          return `${qty} ${w.unit} ${w.trade}: ₹${subtotal.toLocaleString()}`;
                        }).join(" + ")})
                      </span>
                    </div>
                  )}
                </>
              ) : null}

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Amount (₹) *</label>
               {(form.billing_mode === "labour" || form.billing_mode === "measurement") && !showAmountInput ? (
                  // Read-only computed display by default — reads as "here's
                  // the answer", not "please type a number", since this is
                  // already fully derived from headcount × rate × days above.
                  <div className="flex items-center justify-between border border-slate-200 rounded-xl p-3 bg-slate-50">
                    <span className="text-lg font-bold text-slate-800">
                      ₹{Number(form.amount || 0).toLocaleString()}
                    </span>
                    <button type="button" onClick={() => setShowAmountInput(true)}
                      className="text-xs text-blue-600 font-semibold">
                      ✏️ Edit
                    </button>
                  </div>
                ) : (
                  <input type="number" min="0" value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                )}
                {form.billing_mode === "labour" && (
                  <p className="text-[11px] text-slate-400 mt-1">Auto-calculated from headcount × rate × days.</p>
                )}
                {form.billing_mode === "measurement" && (
                  <p className="text-[11px] text-slate-400 mt-1">Auto-calculated from confirmed qty × rate per trade.</p>
                )}
              </div>
            </>
          )}

          <button onClick={handleSubmit} disabled={saving}
            className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-40">
            {saving ? "Saving..." : "Log Work"}
          </button>

        </div>
      )}
    </div>
  );
}
